import { Link } from "react-router-dom";
import { Activity } from "react-feather";
import { TableColumn } from "../../../newcomponents/table/types";
import { DateString } from "../../../components/dateString";
import { formatStrings } from "../../../utils/time";
import { formSetValue } from "../../gvc/testNewForm/utils";
import { ProcessedNode } from "./nodeUtils";

interface NodeColumnOptions {
  provider: string;
  nodePoolNames: Set<string>;
  basePath: string;
  formId: string;
  nodePoolIdMap: Map<string, string>;
  onNodeClick?: (node: ProcessedNode) => void;
}

export function getNodeColumns(opts?: NodeColumnOptions): TableColumn<ProcessedNode>[] {
  return [
    // 1. Name (fixed, cannot hide)
    {
      id: "name",
      label: "Name",
      size: 300,
      enableResize: true,
      enableSort: true,
      accessorFn: (row) => row.name,
      cell: (p) => (
        <button
          type="button"
          className="color-link cursor-pointer"
          style={{
            fontFamily: "input-mono-compressed, monospace",
            fontSize: 12,
            background: "none",
            border: "none",
            padding: 0,
          }}
          onClick={() => opts?.onNodeClick?.(p.row.original)}
        >
          {p.row.original.name}
        </button>
      ),
    },
    // 2. Status (fixed, cannot hide)
    {
      id: "status",
      label: "Status",
      size: 220,
      enableSort: true,
      enableResize: true,
      accessorFn: (row) => row.statusLabel,
      cell: (p) => {
        const node = p.row.original;
        const label = node.statusLabel;
        const conditionTypes = node.conditions.map((c) => c.type).join(",");
        const displayLabel = conditionTypes ? `${label},${conditionTypes}` : label;
        const color =
          label === "Ready"
            ? "var(--color-action)"
            : label.startsWith("Ready,")
              ? "#f59e0b"
              : "var(--color-danger)";

        return (
          <div className="flex items-center">
            <Activity style={{ width: 16, marginRight: "0.25rem", color }} className="h-4" />
            <span>{displayLabel}</span>
          </div>
        );
      },
    },
    // 3. Age
    {
      id: "age",
      label: "Age",
      size: 150,
      enableSort: true,
      enableHide: true,
      sortingFn: "cplnDate",
      accessorFn: (row) => row.creationTimestamp,
      cell: (p) => {
        const iso = p.row.original.creationTimestamp;
        if (!iso) return <span>-</span>;
        return <DateString iso={iso} format={formatStrings.humanize} />;
      },
    },
    // 4. Version
    {
      id: "version",
      label: "Version",
      size: 180,
      enableSort: true,
      enableHide: true,
      hiddenByDefault: true,
      accessorFn: (row) => row.kubeletVersion,
      cell: (p) => <span>{p.row.original.kubeletVersion || "-"}</span>,
    },
    // 5. Internal IP
    {
      id: "internalIP",
      label: "Internal IP",
      size: 140,
      enableSort: true,
      enableHide: true,
      hiddenByDefault: true,
      accessorFn: (row) => row.internalIP,
      cell: (p) => (
        <span style={{ fontFamily: "input-mono-compressed, monospace", fontSize: 12 }}>
          {p.row.original.internalIP || "-"}
        </span>
      ),
    },
    // 6. External IP
    {
      id: "externalIP",
      label: "External IP",
      size: 140,
      enableSort: true,
      enableHide: true,
      hiddenByDefault: true,
      accessorFn: (row) => row.externalIP,
      cell: (p) => (
        <span style={{ fontFamily: "input-mono-compressed, monospace", fontSize: 12 }}>
          {p.row.original.externalIP || "-"}
        </span>
      ),
    },
    // 7. OS Image
    {
      id: "osImage",
      label: "OS Image",
      size: 250,
      enableSort: true,
      enableHide: true,
      hiddenByDefault: true,
      accessorFn: (row) => row.osImage,
      cell: (p) => <span>{p.row.original.osImage || "-"}</span>,
    },
    // 8. Kernel Version
    {
      id: "kernelVersion",
      label: "Kernel Version",
      size: 250,
      enableSort: true,
      enableHide: true,
      hiddenByDefault: true,
      accessorFn: (row) => row.kernelVersion,
      cell: (p) => <span>{p.row.original.kernelVersion || "-"}</span>,
    },
    // 9. Zone
    {
      id: "zone",
      label: "Zone",
      size: 150,
      enableSort: true,
      enableHide: true,
      hiddenByDefault: true,
      accessorFn: (row) => row.zone,
      cell: (p) => <span>{p.row.original.zone || "-"}</span>,
    },
    // 10. Instance Type
    {
      id: "instanceType",
      label: "Instance Type",
      size: 140,
      enableSort: true,
      enableHide: true,
      accessorFn: (row) => row.instanceType,
      cell: (p) => <span>{p.row.original.instanceType || "-"}</span>,
    },
    // 11. Node Pool
    {
      id: "nodePool",
      label: "Node Pool",
      size: 140,
      enableSort: true,
      enableHide: true,
      accessorFn: (row) => row.nodePool,
      cell: (p) => {
        const pool = p.row.original.nodePool;
        if (pool === "__unassigned__") return <span>Unassigned</span>;
        if (opts?.basePath && opts.provider && opts.nodePoolNames?.has(pool)) {
          return (
            <Link
              to={`${opts.basePath}/node-pools-${opts.provider}`}
              className="color-link"
              onClick={() => {
                const poolId = opts.nodePoolIdMap.get(pool);
                if (poolId) {
                  formSetValue(opts.formId, `meta.${opts.provider}.activeKeyNodePool`, poolId);
                }
              }}
            >
              {pool}
            </Link>
          );
        }
        return <span>{pool}</span>;
      },
    },
    // 12. Arch
    {
      id: "arch",
      label: "Arch",
      size: 100,
      enableSort: true,
      enableHide: true,
      accessorFn: (row) => row.architecture,
      cell: (p) => <span>{p.row.original.architecture || "-"}</span>,
    },
    // 13. Allocatable CPU
    {
      id: "allocatableCpu",
      label: "Alloc. CPU",
      size: 120,
      enableSort: true,
      enableHide: true,
      accessorFn: (row) => row.allocatableCpu,
      cell: (p) => <span>{p.row.original.allocatableCpu || "-"}</span>,
    },
    // 14. Allocatable Memory
    {
      id: "allocatableMemory",
      label: "Alloc. Memory",
      size: 140,
      enableSort: true,
      enableHide: true,
      accessorFn: (row) => row.allocatableMemory,
      cell: (p) => <span>{p.row.original.allocatableMemory || "-"}</span>,
    },
  ];
}
